import { VISION_CAP_TILES } from "../vision/vision.ts";

/**
 * Playback's pure rules (ADR-085 decisions 1, 8 and 9, and its amendments for P1.9-T77 and P1.12-T10): the variant a
 * hook plays, the level and pan of a positional sound inside the vision rectangle, another player's sound by its
 * distance, the level and pan a placed sound has for a listener who has moved since it started, and a bus's gain from
 * its volume.
 */

/**
 * The fraction of the vision rectangle's half extent within which a positional sound plays at full level.
 */
export const INNER_FRACTION = 0.2;

/**
 * The farthest a positional sound pans to either side.
 */
export const MAX_PAN = 0.8;

const HALF_WIDTH = VISION_CAP_TILES.width / 2;
const HALF_HEIGHT = VISION_CAP_TILES.height / 2;

/**
 * Picks a variant from a roll, never the one played last while there are two or more.
 *
 * @param count How many variants the hook has, at least 1.
 * @param last The variant played last, or -1 for none.
 * @param roll A number from 0 to 1, the page's random source.
 * @returns The variant's index.
 * @example
 * pickVariant(3, 1, 0.6);
 * // 2: the roll picks among 0 and 2, skipping 1
 */
export function pickVariant(count: number, last: number, roll: number): number {
  if (count <= 1) {
    return 0;
  }
  const excluded = last >= 0 && last < count;
  const choices = excluded ? count - 1 : count;
  const pick = Math.min(choices - 1, Math.floor(Math.max(0, roll) * choices));
  return excluded && pick >= last ? pick + 1 : pick;
}

/**
 * The level and pan of a sound from a source at an offset from the listener: full within `INNER_FRACTION` of the
 * vision rectangle's half extent on the farther axis, falling linearly to silence at its edge, panned by the
 * horizontal offset over the half width, at most `MAX_PAN`.
 *
 * @param dx The source's offset across, in tiles, right positive.
 * @param dy The source's offset down, in tiles.
 * @param out Where the level, 0 to 1, and the pan, -1 to 1, are written.
 * @returns False, writing nothing, when the source lies outside the vision rectangle.
 */
export function positional(dx: number, dy: number, out: { gain: number; pan: number }): boolean {
  const across = Math.abs(dx) / HALF_WIDTH;
  const down = Math.abs(dy) / HALF_HEIGHT;
  if (across > 1 || down > 1) {
    return false;
  }
  const reach = Math.max(across, down);
  out.gain = reach <= INNER_FRACTION ? 1 : (1 - reach) / (1 - INNER_FRACTION);
  out.pan = Math.max(-1, Math.min(1, dx / HALF_WIDTH)) * MAX_PAN;
  return true;
}

/**
 * The distance in tiles within which another player's sound plays at full level (the author's ruling of 2026-10-03,
 * ADR-085's amendment for P1.9-T77).
 */
export const PLAYER_SOUND_FULL_TILES = 1;

/**
 * The distance in tiles at which another player's sound has faded to silence; at or beyond it nothing plays.
 */
export const PLAYER_SOUND_REACH_TILES = 5;

/**
 * The level of another player's sound at a distance: full within `PLAYER_SOUND_FULL_TILES`, then an eased fade (a
 * smoothstep, flat at both ends) to silence at `PLAYER_SOUND_REACH_TILES` and beyond.
 *
 * @param tiles The straight distance from the listener, in tiles.
 * @returns The level from 0 to 1.
 * @example
 * playerFalloff(3);
 * // 0.5, halfway through the fade
 */
export function playerFalloff(tiles: number): number {
  if (tiles <= PLAYER_SOUND_FULL_TILES) {
    return 1;
  }
  if (tiles >= PLAYER_SOUND_REACH_TILES) {
    return 0;
  }
  const t = (tiles - PLAYER_SOUND_FULL_TILES) / (PLAYER_SOUND_REACH_TILES - PLAYER_SOUND_FULL_TILES);
  return 1 - t * t * (3 - 2 * t);
}

/**
 * The level and pan of another player's sound: its level by `playerFalloff` over the straight distance, its pan as
 * `positional` pans, so a sound at or beyond the reach starts nothing.
 *
 * @param dx The source's offset across, in tiles, right positive.
 * @param dy The source's offset down, in tiles.
 * @param out Where the level, 0 to 1, and the pan, -1 to 1, are written.
 * @returns False, writing nothing, when the source lies at or beyond `PLAYER_SOUND_REACH_TILES`.
 */
export function playerPositional(dx: number, dy: number, out: { gain: number; pan: number }): boolean {
  const tiles = Math.hypot(dx, dy);
  if (!(tiles < PLAYER_SOUND_REACH_TILES)) {
    return false;
  }
  out.gain = playerFalloff(tiles);
  out.pan = Math.max(-1, Math.min(1, dx / HALF_WIDTH)) * MAX_PAN;
  return true;
}

/**
 * The level and pan of a sound that stays where it started, for a listener wherever they now are (ADR-085's amendment
 * for P1.12-T10): `positional`'s, or `playerPositional`'s for a sound that fades by distance, at the offset from the
 * listener to the source, the level under the hook's trim. A source out of reach is silent and keeps the pan it had.
 *
 * @param byDistance True for a sound that fades by its straight distance (another player's), false for one heard
 *   across the vision rectangle.
 * @param sourceX Where the source is across, in tiles.
 * @param sourceY The same down.
 * @param listenerX Where the listener is across, in tiles.
 * @param listenerY The same down.
 * @param trim The level the hook plays at, 0 to 1 (`hookTrim`).
 * @param out Where the level, 0 to 1, and the pan, -1 to 1, are written; the pan is left as it is when the source is
 *   out of reach.
 * @example
 * placedLevel(false, 33, 35, 33, 27, 1, out);
 * // out.gain is 0: the source is 8 tiles down, past the rectangle's half height of 7.5
 */
export function placedLevel(byDistance: boolean, sourceX: number, sourceY: number, listenerX: number, listenerY: number, trim: number, out: { gain: number; pan: number }): void {
  const dx = sourceX - listenerX;
  const dy = sourceY - listenerY;
  if (byDistance ? playerPositional(dx, dy, out) : positional(dx, dy, out)) {
    out.gain *= trim;
  } else {
    out.gain = 0;
  }
}

/**
 * A bus's gain from its volume: the square of the fraction of 100, a perceptual curve, or 0 while muted.
 *
 * @param volume The volume from 0 to 100; anything outside is held to it and anything not a number is 0.
 * @param muted Whether the bus, or everything, is muted.
 * @returns The gain from 0 to 1.
 */
export function busGain(volume: number, muted: boolean): number {
  if (muted || !(volume > 0)) {
    return 0;
  }
  const fraction = Math.min(volume, 100) / 100;
  return fraction * fraction;
}
